<!-- Modern Onboarding Container -->
@if (validOnboarding && this.tenants.length > 0) {
  <div class="onboarding-container">
    <div class="onboarding-wrapper">
      <!-- Header Section -->
      <div class="onboarding-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="pi pi-cloud"></i>
          </div>
          <div class="header-text">
            <h1 class="header-title">{{ dataService.getTexts().OnboardingPageTitle }}</h1>
            <p class="header-subtitle">{{ dataService.getTexts().OnboardingPageSubtitle }}</p>
          </div>
        </div>
      </div>
      <!-- Progress Steps -->
      <div class="progress-section">
        <div class="progress-container">
          <!-- Desktop Progress Steps -->
          <div class="progress-steps desktop-steps">
            @for (step of steps; track step; let i = $index) {
              <div
                class="progress-step"
                [ngClass]="{
                  completed: step.status === 'completed',
                  active: currentStepIndex === i,
                  error: step.status === 'error',
                  pending: step.status === 'pending',
                }"
              >
                <div class="step-indicator">
                  <div class="step-icon">
                    @if (step.status === 'completed') {
                      <i class="pi pi-check"></i>
                    }
                    @if (step.status === 'error') {
                      <i class="pi pi-exclamation-triangle"></i>
                    }
                    @if (step.status === 'in-progress') {
                      <i class="pi pi-spin pi-spinner"></i>
                    }
                    @if (step.status === 'pending') {
                      <span>{{ i + 1 }}</span>
                    }
                  </div>
                </div>
                <div class="step-info">
                  <span class="step-title">{{ step.title }}</span>
                  <span class="step-description">{{ step.description }}</span>
                </div>
              </div>
            }
          </div>
          <!-- Mobile Progress Steps -->
          <div class="progress-steps mobile-steps">
            <div class="current-step-info">
              <div class="current-step-indicator">
                <div class="step-icon">
                  @if (steps[currentStepIndex].status === 'completed') {
                    <i class="pi pi-check"></i>
                  }
                  @if (steps[currentStepIndex].status === 'error') {
                    <i class="pi pi-exclamation-triangle"></i>
                  }
                  @if (steps[currentStepIndex].status === 'in-progress') {
                    <i class="pi pi-spin pi-spinner"></i>
                  }
                  @if (steps[currentStepIndex].status === 'pending') {
                    <span>{{ currentStepIndex + 1 }}</span>
                  }
                </div>
              </div>
              <div class="current-step-text">
                <span class="step-title">{{ steps[currentStepIndex].title }}</span>
                <span class="step-subtitle">{{ getMobileStepOfText() }}</span>
              </div>
            </div>
            <div class="step-progress-bar">
              <div class="progress-fill" [style.width.%]="((currentStepIndex + 1) / steps.length) * 100"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- Main Content Area -->
      <div class="content-section">
        <div class="content-card">
          <!-- Step 0: Setup -->
          @if (currentStepIndex === 0) {
            <div class="step-content setup-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingSetupStepTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingSetupStepDescription }}
                </p>
              </div>
              <div class="info-banner">
                <div class="info-icon">
                  <i class="pi pi-info-circle"></i>
                </div>
                <div class="info-content">
                  <strong>{{ dataService.getTexts().OnboardingImportantLabel }}</strong> {{ dataService.getTexts().OnboardingOrgNameImmutableWarning }}
                </div>
              </div>
              <form [formGroup]="dataFormGroup" class="organization-form">
                <div class="form-group">
                  <label for="orgName" class="form-label">{{ dataService.getTexts().OnboardingOrgNameLabel }}</label>
                  <div class="input-wrapper">
                    <i class="pi pi-building input-icon"></i>
                    <input
                      type="text"
                      formControlName="orgName"
                      pInputText
                      id="orgName"
                      class="form-input"
                      [placeholder]="dataService.getTexts().OnboardingOrgNamePlaceholder"
                    />
                  </div>
                  @if (dataFormGroup.controls['orgName'].invalid && dataFormGroup.controls['orgName'].touched) {
                    <div class="error-message">
                      <i class="pi pi-exclamation-circle"></i>
                      {{ getErrorMessage() }}
                    </div>
                  }
                </div>
                <div class="form-group">
                  <label for="licenseKey" class="form-label">{{ dataService.getTexts().OnboardingLicenseKeyLabel }}</label>
                  <div class="input-wrapper">
                    <i class="pi pi-key input-icon"></i>
                    <input
                      type="text"
                      formControlName="licenseKey"
                      pInputText
                      id="licenseKey"
                      class="form-input"
                      [placeholder]="dataService.getTexts().OnboardingLicenseKeyPlaceholder"
                    />
                  </div>
                  @if (dataFormGroup.controls['licenseKey'].invalid && dataFormGroup.controls['licenseKey'].touched) {
                    <div class="error-message">
                      <i class="pi pi-exclamation-circle"></i>
                      {{ dataService.getTexts().OnboardingLicenseKeyInvalid }}
                    </div>
                  }
                </div>
              </form>
              <div class="tenant-selection">
                <h3 class="section-title">{{ dataService.getTexts().OnboardingSelectTenantTitle }}</h3>
                <div class="tenant-grid">
                  @for (tenant of tenants; track tenant) {
                    <div
                      class="tenant-card"
                      [ngClass]="{ selected: currentlySelected?.tenantId === tenant.tenantId }"
                      (click)="selectedTenant(tenant)"
                    >
                      <div class="tenant-info">
                        <div class="tenant-icon">
                          <i class="pi pi-building" aria-hidden="true"></i>
                        </div>
                        <div class="tenant-details">
                          <span class="tenant-name">{{ tenant.displayName }}</span>
                          <span class="tenant-id">{{ tenant.tenantId }}</span>
                        </div>
                      </div>
                      <div class="tenant-status">
                        @if (currentlySelected?.tenantId === tenant.tenantId) {
                          <div class="selected-indicator">
                            <i class="pi pi-check"></i>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonContinue"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  [disabled]="!dataFormGroup.valid || !currentlySelected"
                  (onClick)="setupStep()"
                  severity="primary"
                />
              </div>
            </div>
          }
          <!-- Step 1: Portal Consent -->
          @if (currentStepIndex === 1) {
            <div class="step-content consent-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingStepPortalConsentTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingPortalConsentStepDescription }}
                </p>
              </div>
              <div class="consent-card">
                <div class="consent-icon">
                  <i class="pi pi-shield"></i>
                </div>
                <div class="consent-content">
                  <h3>{{ dataService.getTexts().OnboardingAdminConsentRequired }}</h3>
                  <p>
                    {{ dataService.getTexts().OnboardingAdminConsentDescription }}
                  </p>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonOpenConsentWindow"
                  icon="pi pi-external-link"
                  iconPos="right"
                  (onClick)="portalConsentStep()"
                  [disabled]="steps[1].status === 'in-progress'"
                  severity="primary"
                />
                @if (steps[1].status === 'in-progress') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonMarkCompleted"
                    icon="pi pi-check"
                    (onClick)="markStepAsCompleted('portal-consent')"
                    severity="success"
                  />
                }
                @if (steps[1].status === 'error') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonRetry"
                    icon="pi pi-refresh"
                    (onClick)="retryStep('portal-consent')"
                    severity="warning"
                  />
                }
              </div>
              @if (steps[1].errorMessage) {
                <div class="error-banner">
                  <div class="error-icon">
                    <i class="pi pi-exclamation-triangle"></i>
                  </div>
                  <div class="error-content">
                    <strong>{{ dataService.getTexts().OnboardingErrorLabel }}</strong> {{ steps[1].errorMessage }}
                    @if (steps[1].debugInfo) {
                      <div class="debug-info">
                        <p-button
                          type="button"
                          (click)="showDebugInfo(steps[1])"
                          [label]="dataService.getTexts().OnboardingButtonShowDebugInfo"
                          severity="secondary"
                          text
                          size="small"
                        />
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
          <!-- Step 2: Backend Consent -->
          @if (currentStepIndex === 2) {
            <div class="step-content consent-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingStepBackendConsentTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingBackendConsentStepDescription }}
                </p>
              </div>
              <div class="consent-card">
                <div class="consent-icon">
                  <i class="pi pi-server"></i>
                </div>
                <div class="consent-content">
                  <h3>{{ dataService.getTexts().OnboardingApiAccessRequired }}</h3>
                  <p>
                    {{ dataService.getTexts().OnboardingApiAccessDescription }}
                  </p>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonOpenConsentWindow"
                  icon="pi pi-external-link"
                  iconPos="right"
                  (onClick)="backendConsentStep()"
                  [disabled]="steps[2].status === 'in-progress'"
                  severity="primary"
                />
                @if (steps[2].status === 'in-progress') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonMarkCompleted"
                    icon="pi pi-check"
                    (onClick)="markStepAsCompleted('backend-consent')"
                    severity="success"
                  />
                }
                @if (steps[2].status === 'error') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonRetry"
                    icon="pi pi-refresh"
                    (onClick)="retryStep('backend-consent')"
                    severity="warning"
                  />
                }
              </div>
              @if (steps[2].errorMessage) {
                <div class="error-banner">
                  <div class="error-icon">
                    <i class="pi pi-exclamation-triangle"></i>
                  </div>
                  <div class="error-content">
                    <strong>{{ dataService.getTexts().OnboardingErrorLabel }}</strong> {{ steps[2].errorMessage }}
                    @if (steps[2].debugInfo) {
                      <div class="debug-info">
                        <p-button
                          type="button"
                          (click)="showDebugInfo(steps[2])"
                          [label]="dataService.getTexts().OnboardingButtonShowDebugInfo"
                          severity="secondary"
                          text
                          size="small"
                        />
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
          <!-- Step 3: Create Service Principal -->
          @if (currentStepIndex === 3) {
            <div class="step-content service-principal-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingStepCreateServicePrincipalTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingCreateSpStepDescription }}
                </p>
              </div>
              <div class="consent-card">
                <div class="consent-icon">
                  <i class="pi pi-cog"></i>
                </div>
                <div class="consent-content">
                  <h3>{{ dataService.getTexts().OnboardingAutomatedManagement }}</h3>
                  <p>{{ dataService.getTexts().OnboardingAutomatedManagementDescription }}</p>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonCreateServicePrincipal"
                  icon="pi pi-cog"
                  iconPos="right"
                  [loading]="steps[3].status === 'in-progress'"
                  (onClick)="createServicePrincipalStep()"
                  [disabled]="steps[3].status === 'in-progress'"
                  severity="primary"
                />
                @if (steps[3].status === 'in-progress') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonCancel"
                    icon="pi pi-times"
                    (onClick)="cancelCurrentOperation()"
                    severity="secondary"
                    text
                  />
                }
                @if (steps[3].status === 'error') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonRetry"
                    icon="pi pi-refresh"
                    (onClick)="retryStep('create-service-principal')"
                    severity="warning"
                  />
                }
              </div>
              @if (steps[3].errorMessage) {
                <div class="error-banner">
                  <div class="error-icon">
                    <i class="pi pi-exclamation-triangle"></i>
                  </div>
                  <div class="error-content">
                    <strong>{{ dataService.getTexts().OnboardingErrorLabel }}</strong> {{ steps[3].errorMessage }}
                    @if (steps[3].debugInfo) {
                      <div class="debug-info">
                        <p-button
                          type="button"
                          (click)="showDebugInfo(steps[3])"
                          [label]="dataService.getTexts().OnboardingButtonShowDebugInfo"
                          severity="secondary"
                          text
                          size="small"
                        />
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
          <!-- Step 4: Service Principal Consent -->
          @if (currentStepIndex === 4) {
            <div class="step-content consent-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingStepServicePrincipalConsentTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingSpConsentStepDescription }}
                </p>
              </div>
              <div class="consent-card">
                <div class="consent-icon">
                  <i class="pi pi-users"></i>
                </div>
                <div class="consent-content">
                  <h3>{{ dataService.getTexts().OnboardingGraphApiAccess }}</h3>
                  <p>
                    {{ dataService.getTexts().OnboardingGraphApiAccessDescription }}
                  </p>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonOpenConsentWindow"
                  icon="pi pi-external-link"
                  iconPos="right"
                  (onClick)="servicePrincipalConsentStep()"
                  [disabled]="steps[4].status === 'in-progress'"
                  severity="primary"
                />
                @if (steps[4].status === 'in-progress') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonMarkCompleted"
                    icon="pi pi-check"
                    (onClick)="markStepAsCompleted('service-principal-consent')"
                    severity="success"
                  />
                }
                @if (steps[4].status === 'error') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonRetry"
                    icon="pi pi-refresh"
                    (onClick)="retryStep('service-principal-consent')"
                    severity="warning"
                  />
                }
              </div>
              @if (steps[4].errorMessage) {
                <div class="error-banner">
                  <div class="error-icon">
                    <i class="pi pi-exclamation-triangle"></i>
                  </div>
                  <div class="error-content"><strong>{{ dataService.getTexts().OnboardingErrorLabel }}</strong> {{ steps[4].errorMessage }}</div>
                </div>
              }
            </div>
          }
          <!-- Step 5: Create Organization -->
          @if (currentStepIndex === 5) {
            <div class="step-content organization-step">
              <div class="step-header">
                <h2 class="step-title">{{ dataService.getTexts().OnboardingStepCreateOrganizationTitle }}</h2>
                <p class="step-description">
                  {{ dataService.getTexts().OnboardingCreateOrgStepDescription }}
                </p>
              </div>
              <div class="configuration-summary">
                <h3 class="summary-title">
                  <i class="pi pi-info-circle"></i>
                  {{ dataService.getTexts().OnboardingConfigurationSummary }}
                </h3>
                <div class="summary-grid">
                  <div class="summary-item">
                    <span class="summary-label">{{ dataService.getTexts().OnboardingSummaryOrgName }}</span>
                    <p-chip [label]="dataFormGroup.controls.orgName.value" severity="primary" />
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">{{ dataService.getTexts().OnboardingSummaryTenant }}</span>
                    <p-chip [label]="currentlySelected?.displayName" severity="info" />
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">{{ dataService.getTexts().OnboardingSummaryLicenseKey }}</span>
                    <p-chip
                      [label]="dataFormGroup.controls.licenseKey.value?.substring(0, 8) + '...'"
                      severity="secondary"
                    />
                  </div>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonCreateOrganization"
                  icon="pi pi-plus"
                  iconPos="right"
                  [loading]="steps[5].status === 'in-progress'"
                  (onClick)="createOrganizationStep()"
                  [disabled]="steps[5].status === 'in-progress'"
                  severity="success"
                />
                @if (steps[5].status === 'in-progress') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonCancel"
                    icon="pi pi-times"
                    (onClick)="cancelCurrentOperation()"
                    severity="secondary"
                    text
                  />
                }
                @if (steps[5].status === 'error') {
                  <p-button
                    [label]="dataService.getTexts().OnboardingButtonRetry"
                    icon="pi pi-refresh"
                    (onClick)="retryStep('create-organization')"
                    severity="warning"
                  />
                }
              </div>
              @if (steps[5].errorMessage) {
                <div class="error-banner">
                  <div class="error-icon">
                    <i class="pi pi-exclamation-triangle"></i>
                  </div>
                  <div class="error-content"><strong>{{ dataService.getTexts().OnboardingErrorLabel }}</strong> {{ steps[5].errorMessage }}</div>
                </div>
              }
            </div>
          }
          <!-- Completion Step -->
          @if (steps[5].status === 'completed') {
            <div class="step-content completion-step">
              <div class="success-card">
                <div class="success-icon">
                  <i class="pi pi-check-circle"></i>
                </div>
                <div class="success-content">
                  <h2 class="success-title">{{ dataService.getTexts().OnboardingCongratulations }}</h2>
                  <p class="success-message">
                    {{ dataService.getTexts().OnboardingSuccessMessagePrefix }} <strong>"{{ dataFormGroup.controls.orgName.value }}"</strong> {{ dataService.getTexts().OnboardingSuccessMessageSuffix }}
                  </p>
                </div>
              </div>
              <div class="step-actions">
                <p-button
                  [label]="dataService.getTexts().OnboardingButtonGoToAdminPortal"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  (onClick)="navigateToAdminPortal()"
                  severity="primary"
                />
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Loading States -->
@if (!validOnboarding || tenants.length === 0) {
  <div class="loading-container">
    <div class="loading-card">
      <div class="loading-spinner">
        <p-progressSpinner
          styleClass="w-4rem h-4rem"
          strokeWidth="3"
          animationDuration=".8s"
          strokeColor="var(--primary-color)"
        />
      </div>
      <div class="loading-content">
        <h3 class="loading-title">{{ dataService.getTexts().OnboardingLoadingTitle }}</h3>
        <p class="loading-message">
          @if (!validOnboarding) {
            <span>{{ dataService.getTexts().OnboardingValidatingInvitation }}</span>
          }
          @if (validOnboarding && tenants.length === 0) {
            <span>{{ dataService.getTexts().OnboardingLoadingTenants }}</span>
          }
        </p>
      </div>
    </div>
  </div>
}
